import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button } from '@workspace/ui/components/Button'
import { ChevronLeft } from 'lucide-react'
import { RoleForm, RoleFormData } from '@/shared/components/form/RoleForm'
import { api, type RoleUpdate } from '@/shared/lib/api'
import { toast } from '@workspace/ui/components/Sonner'
import { buildPermissionStringsFromForm, parsePermissionStringsToForm } from '@/shared/lib/permissions'
import { requirePermissionBeforeLoad } from '@/shared/lib/authorization/routeAuth'

export const Route = createFileRoute('/users/roles/$id/edit')({
    component: EditRolePage,
    beforeLoad: requirePermissionBeforeLoad('operate', 'system'),
})

function EditRolePage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { id } = Route.useParams()

    // Fetch role data for editing
    const { data: role, isLoading: isRoleLoading } = useQuery({
        queryKey: ['role', id],
        queryFn: () => api.role.findOne(id),
        enabled: !!id,
    })

    // Update role mutation
    const updateRoleMutation = useMutation({
        mutationFn: (payload: RoleUpdate) => api.role.update(id, payload),
        onSuccess: () => {
            toast.success({
                title: t('common.status.success'),
                description: t('roles.edit.success'),
            })
        },
        onError: error => {
            toast.error({
                title: t('common.status.error'),
                description: t('roles.edit.error'),
            })
            console.error('Update role error:', error)
        },
    })

    const onSubmit = async (data: RoleFormData) => {
        const permissionKeys = buildPermissionStringsFromForm(data.permissions ?? [])

        const payload: RoleUpdate = {
            displayName: data.roleName,
            description: data.description || null,
            level: data.level ? parseInt(data.level) : null,
            permissionKeys,
        }

        await updateRoleMutation.mutateAsync(payload)
    }

    const handleSaveClick = () => {
        // Trigger form submission via a ref or external trigger
        const form = document.querySelector('form')
        if (form) {
            form.requestSubmit()
        }
    }

    // Prepare default values for the form
    const defaultValues = role
        ? {
              roleName: role.displayName.trim(),
              description: (role.description || '').trim(),
              level: role.level && role.level > 0 ? role.level.toString() : '',
              permissions: parsePermissionStringsToForm(role.permissions || []),
          }
        : undefined

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex gap-3 items-center">
                <Link className="p-2 border rounded-[8px]" to="/users/roles">
                    <ChevronLeft size={20} />
                </Link>
                <h1 className="text-2xl font-bold">{t('roles.edit.title')}</h1>
                <Button
                    variant="default"
                    onPress={handleSaveClick}
                    isDisabled={updateRoleMutation.isPending || isRoleLoading}
                    className="min-w-[100px] ml-auto"
                >
                    {updateRoleMutation.isPending ? t('common.actions.saving') : t('common.actions.save')}
                </Button>
            </div>

            <RoleForm
                defaultValues={defaultValues}
                onSubmit={onSubmit}
                isSubmitting={updateRoleMutation.isPending || isRoleLoading}
            />
        </div>
    )
}
